import { useMemo } from 'react';
import { useAccess } from './useAccess';
import { useApprovalModuleAccess } from './useApprovalModuleAccess';
import { useModuleEntitled } from './useModuleLabel';

export type NavAccess = {
  customers: boolean;
  vendors: boolean;
  products: boolean;
  orders: boolean;
  salesOrders: boolean;
  purchaseOrders: boolean;
  staff: boolean;
  roles: boolean;
  approvalFlows: boolean;
  warehouse: boolean;
  storage: boolean;
  chambers: boolean;
  floors: boolean;
  racks: boolean;
  inventory: boolean;
  transport: boolean;
  labour: boolean;
  tasks: boolean;
  settings: boolean;
  categories: boolean;
  units: boolean;
};

/**
 * Single source of truth for desktop sidebar, mobile bottom bar,
 * drawer, More tiles, and Home dashboard links.
 */
export function useNavAccess(): NavAccess {
  const { can } = useAccess();
  const customers = useModuleEntitled('customer');
  const vendors = useModuleEntitled('vendor');
  const products = useModuleEntitled('product');
  const approvalModules = useApprovalModuleAccess();

  return useMemo(() => {
    const salesOrders = can('procurement.sales.view');
    const purchaseOrders = can('procurement.purchase.view');
    const chambers = can('chamber.view');
    const floors = can('floor.view');
    const racks = can('rack.view');
    const roles = can('roles.view');

    return {
      customers,
      vendors,
      products,
      salesOrders,
      purchaseOrders,
      orders: salesOrders || purchaseOrders,
      staff: can('staff.view'),
      roles,
      approvalFlows: roles && approvalModules.anyEnabled,
      warehouse: can('warehouse.view'),
      chambers,
      floors,
      racks,
      storage: chambers || floors || racks,
      inventory: can('inventory.view'),
      transport: can('transport.view'),
      labour: can('labour.view'),
      tasks: true,
      settings: true,
      categories: can('category.view'),
      units: can('units.view'),
    };
  }, [can, customers, vendors, products, approvalModules.anyEnabled]);
}
